/*
 * The confirmation in front of a send (docs/2026-09-20-send-on-approve-ui-shape.md). Pure and
 * timer-injected so the page and the tests share one implementation, the same shape as the hold in
 * send-approve.ts, which this sits in front of.
 *
 * Dawid, 2026-09-21: "accept on draft should gave additional confirmation on click, to avoid
 * sending on misclicks". So a send is two deliberate actions, and this module owns the gap.
 *
 * Two independent guards, because either alone can be defeated by geometry:
 *
 * 1. `armingMs`. A confirmation that has just opened does not accept a confirm at all. The second
 *    half of a double click arrives inside that window and lands on nothing, wherever it falls and
 *    whatever the layout puts under the pointer.
 * 2. The subject. A confirmation opens on one exact description of what the send would do, and
 *    confirms only against the same one. A draft edited, a newer reply, an approval gone stale or
 *    anything that blocks the step while the panel sits open closes it and sends nothing. There is
 *    no "confirm anyway": an unclear state is a closed gate.
 *
 * Nothing here sends. `proceed` hands over to the existing hold, whose 10-second Undo remains the
 * second safety net, and the server-side gate is untouched by any of it.
 */
import type { PlanStep } from "../../../skills/leadcue-reply/scripts/lib/work-plan.ts";
import type { SendStrip } from "./send-approve.ts";

/**
 * How long an open confirmation refuses to confirm. Above the interval a double click arrives in,
 * and far below the time it takes to read the panel, so only a second click nobody meant is lost.
 */
export const CONFIRM_ARMING_MS = 450;

export type SendConfirmState =
  | { kind: "closed" }
  /** `subject` is what the send was described as when this opened; it is never re-read. */
  | { kind: "open"; armed: boolean; subject: string };

/** `stale` is never the rep's doing: the conversation moved while the panel was open. */
type ConfirmCancel = "cancel" | "stale";

export type SendConfirmPorts = {
  /** Runs at most once per opened confirmation, and only from an armed, unmoved one. */
  proceed: () => void;
  onState: (state: SendConfirmState) => void;
  onCancelled: (reason: ConfirmCancel) => void;
  armingMs?: number;
  /** Injected so tests arm on demand instead of waiting. */
  arm?: (ready: () => void, ms: number) => () => void;
};

/*
 * Every entry point is told what a send would do right now rather than reaching for it. The caller
 * holds that fact already — in the page it is the current render's — so the gate never reads a
 * description of the conversation that has gone out of date underneath it.
 */
export type SendConfirm = {
  state: () => SendConfirmState;
  /** The first deliberate action. Inert while already open, and refused when there is no subject. */
  open: (subject: string | null) => void;
  /** The second. Does nothing unless armed, and nothing unless the send is still the same one. */
  confirm: (subject: string | null) => void;
  cancel: (reason: ConfirmCancel) => void;
  /** Called when the page re-renders: a subject that moved voids the open confirmation. */
  sync: (subject: string | null) => void;
  dispose: () => void;
};

const afterDelay = (ready: () => void, ms: number): (() => void) => {
  const timer = setTimeout(ready, ms);
  return () => clearTimeout(timer);
};

export function createSendConfirm(ports: SendConfirmPorts): SendConfirm {
  const armingMs = ports.armingMs ?? CONFIRM_ARMING_MS;
  const arm = ports.arm ?? afterDelay;
  let state: SendConfirmState = { kind: "closed" };
  let disarm: (() => void) | null = null;
  let closed = false;
  /*
   * Bumped by each `open`, so an arming timer left over from an earlier panel cannot arm the one
   * that replaced it. A timer outliving its own panel is caught by the state test instead, and
   * `shut` deliberately does not bump: a redundant guard in a gate like this reads as if it were
   * load-bearing, and the next reader cannot tell which one is.
   */
  let generation = 0;

  const set = (next: SendConfirmState): void => {
    state = next;
    ports.onState(next);
  };
  const stopArming = (): void => {
    disarm?.();
    disarm = null;
  };
  const shut = (reason: ConfirmCancel): void => {
    stopArming();
    set({ kind: "closed" });
    ports.onCancelled(reason);
  };

  return {
    state: () => state,
    open(subject) {
      // A second press while the panel is open is not a confirmation and never re-arms this one.
      if (closed || state.kind === "open") return;
      if (subject === null) return;
      const token = ++generation;
      set({ kind: "open", armed: false, subject });
      disarm = arm(() => {
        if (closed || token !== generation || state.kind !== "open") return;
        set({ kind: "open", armed: true, subject: state.subject });
      }, armingMs);
    },
    confirm(subject) {
      if (closed || state.kind !== "open" || !state.armed) return;
      // Tested again here: the panel may have sat open while the conversation moved underneath it.
      if (subject !== state.subject) {
        shut("stale");
        return;
      }
      stopArming();
      set({ kind: "closed" });
      ports.proceed();
    },
    cancel(reason) {
      if (closed || state.kind !== "open") return;
      shut(reason);
    },
    sync(subject) {
      if (closed || state.kind !== "open") return;
      if (subject === state.subject) return;
      shut("stale");
    },
    dispose() {
      // Leaving the page with the panel open: nothing was confirmed, so nothing was sent.
      // `closed` is tested first by every entry point, so nothing else needs resetting.
      closed = true;
      stopArming();
    },
  };
}

type ConfirmKeyPress = {
  key: string;
  metaKey: boolean;
  ctrlKey: boolean;
  altKey: boolean;
  shiftKey?: boolean;
  repeat?: boolean;
};

/**
 * The keys an open panel answers itself, kept beside the workspace's other keyboard model in
 * shortcuts.ts. Escape backs out; nothing confirms.
 *
 * No key is the second action, deliberately. Confirming takes moving focus to the confirm control
 * and activating it, so an Enter held down from before the panel opened, or pressed twice out of
 * habit, reaches nothing: focus lands on the panel itself, which answers neither.
 */
export function confirmKeyAction(press: ConfirmKeyPress): "cancel" | null {
  if (press.metaKey || press.ctrlKey || press.altKey) return null;
  return press.key === "Escape" ? "cancel" : null;
}

/**
 * One line naming everything a send depends on. Any difference at all voids an open confirmation,
 * so this errs towards saying "moved": a rep confirming a send they can no longer see the terms of
 * is the failure this exists to prevent, and re-opening the panel costs one click.
 *
 * `null` means no send is possible right now — the step is blocked, or the strip cannot say who the
 * reply would reach — and a confirmation neither opens nor survives on it.
 */
export function sendSubject(input: {
  strip: SendStrip;
  step: PlanStep;
  planRevision: number;
  approved: boolean;
  blocked: boolean;
}): string | null {
  const { strip, step, planRevision, approved, blocked } = input;
  if (blocked || strip.kind !== "ready") return null;
  return [
    `plan:${planRevision}`,
    `step:${step.id}`,
    `state:${step.state}`,
    `approved:${approved ? "yes" : "no"}`,
    `draft:${strip.draftRevision}`,
    `from:${strip.from}`,
    `to:${strip.to}`,
    `subject:${strip.subject}`,
    // A file added, removed or replaced under an open confirmation is a different send.
    `files:${strip.files.map((file) => `${file.fileName}:${file.bytes}`).join("/")}`,
  ].join("\u0000");
}
